import {ChatFragment, ChatFragmentProps} from "../shared/messages/chat/ChatFragment";

import {ChatPersonaType} from "../../toktiv-common/types";
import {
  ChatMessage,
  ChatMessages,
  MessageFactory,
  ParticipantKey,
  ParticipantType
} from "../shared/messages/chat/types";
import {ListItemChange, OnListItemsListener} from "../../shared/types";
import {BaseFragment, BaseFragmentState} from "../../shared/BaseFragment";
import React, {ReactElement} from "react";
import {App} from "../App";
import {ChatAgent} from "./ChatAgent";
import {StyledBoxRow} from "../../shared/StyledComponents";
import {Button, CircularProgress, Typography} from "@mui/material";
import {AppPrefs} from "../app_prefs";
import {PD_MD} from "../../shared/dimens";

export type AppChatFragmentProps = ChatFragmentProps & {
  personaType: ChatPersonaType,
}

export type AppChatFragmentState = BaseFragmentState & {
  messages: ChatMessage[],
  chatBarElement?: ReactElement,
  chatBarStatus?: ReactElement,
  audioPlaying?: boolean,
}

export class AppChatFragment extends BaseFragment<AppChatFragmentProps, AppChatFragmentState> implements OnListItemsListener<ChatMessage> {

  private readonly audio = App.getAppRefs().audio;
  private readonly agent: ChatAgent = new ChatAgent(this.props.myParticipantKey);

  private messages: ChatMessages;

  protected onCreateState(): AppChatFragmentState {
    return {
      ...super.onCreateState(),
      messages: [],
    };
  }

  componentDidUpdate(prevProps: Readonly<AppChatFragmentProps>, prevState: Readonly<AppChatFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.messages.length !== this.state.messages.length && this.state.messages[this.state.messages.length - 1]?.from.id === this.props.myParticipantKey.id) {
      this.setState({
        chatBarElement: <StyledBoxRow style={{
          width: "100%",
          height: "100%",
          alignItems: "center",
          justifyContent: "center"
        }}><CircularProgress/><Typography>Thinking...</Typography></StyledBoxRow>
      })
      this.generateChat()
        .finally(() => this.setState({
          chatBarElement: null,
        }));
    }
    if (prevState.audioPlaying !== this.state.audioPlaying) {
      this.setState({
        chatBarStatus: this.state.audioPlaying
          ? <StyledBoxRow style={{padding: PD_MD, alignItems: "center", justifyContent: "center"}}>
            <Typography>Speaking...</Typography>
            <Button
              variant="contained"
              onClick={() => this.audio.pause()}>
              Stop
            </Button>
          </StyledBoxRow>
          : null,
      })
    }
  }

  private async generateChat() {
    const resp = await this.agent.chat(this.props.personaType, this.state.messages);//.filter(message => message.from.id === this.props.myParticipantKey.id || message.from.id === this.props.personaType));
    await this.playText(resp);
    await this.messages.addListItem(MessageFactory.createTextMessage(this.props.thread.id, new ParticipantKey(this.props.personaType, ParticipantType.MEMBER), resp));
  };

  private async playText(text: string) {
    const encodedFile = await this.agent.textToSpeech(this.props.personaType, text);
    this.audio.src = "data:audio/mp3;base64," + encodedFile;
    this.audio.play();
  }

  componentDidMount() {
    // We need to add this here, as sometimes we get the mount -> unmount -> mount sequence, so we need to re-register
    // with messages.
    this.messages?.registerObserver(this);
    super.componentDidMount();
    this.audio.addEventListener("play", this.onPlayStateChanged.bind(this));
    this.audio.addEventListener("pause", this.onPlayStateChanged.bind(this));
    this.audio.addEventListener("ended", this.onPlayStateChanged.bind(this));
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.messages?.unregisterObserver(this);
    this.audio.removeEventListener("play", this.onPlayStateChanged.bind(this));
    this.audio.removeEventListener("pause", this.onPlayStateChanged.bind(this));
    this.audio.removeEventListener("ended", this.onPlayStateChanged.bind(this));
  }

  onPlayStateChanged(): void {
    this.setState({
      audioPlaying: !this.audio.paused,
    });
  }

  protected async fetchOnMount(): Promise<void> {
    if (this.messages) {
      this.messages.unregisterObserver(this);
    }
    this.messages = ChatMessages.getInstance(this.props.myParticipantKey.type, this.props.thread.id);
    this.setState({
      messages: await this.messages.getOrLoadListItems(),
    });
    this.messages.registerObserver(this);
  }

  onItemChanged(item: ChatMessage, change: ListItemChange): void {
    if (change === ListItemChange.ADDED && item.from.id === this.props.myParticipantKey.id) {
      this.setState({
        messages: this.messages.getListItems(),
      });
    }
  }

  protected renderContainerContent(): React.ReactElement | null {
    return <div style={{display: "flex", flexDirection: "column", height: "100vh"}}>
      <ChatFragment
        chatBarElement={this.state.chatBarElement}
        chatBarStatus={this.state.chatBarStatus}
        chatBarConfig={{
          showVoiceInputButton: true,
          voiceAutoSend: AppPrefs.getInstance().getAutosendChat(),
          onVoiceInputToText: url => this.agent.speechToText(url)
        }}
        {...this.props}/>
    </div>;
  }
}